/**
 * TileCard — renders a single GlanceViewModel into a grid cell.
 *
 * This is the H3 rendering surface: it receives a view-model and renders it.
 * It never imports a concrete tile adapter. All state (loading / ok / stale /
 * error / rateLimited) is handled here from the view-model's dataState/glanceState.
 *
 * glance-grammar-spec §1.1: one primary fact, one delta, one state color.
 * §3.1: type scale tokens. §4.1: tint + left-edge accent. §6: state chrome.
 *
 * Immutability: functional component — no mutation of props or state.
 */
import { useState } from 'react';
import type { GlanceViewModel } from 'briefpane/contracts';
import type { TileSize } from 'briefpane/schema';

interface TileCardProps {
  readonly viewModel: GlanceViewModel;
  readonly size: TileSize;
  /** Stagger index for the board-load fade-in animation (§5.1). */
  readonly staggerIndex: number;
  /** When true, shows the remove affordance (two-tap confirm). */
  readonly editMode?: boolean;
  /** Called when the user confirms tile removal. */
  readonly onRemove?: () => void;
  /**
   * Optional attribution line for tiles that legally require it (D-010).
   * E.g. for sports.score: "TheSportsDB" (shown as a tiny dim link in the
   * freshness/source area). Glance grammar is preserved — attribution lives
   * in the freshness row, not a competing focal point.
   */
  readonly attribution?: { readonly text: string; readonly url: string } | undefined;
}

/**
 * Map a GlanceState to the CSS custom-property values for tint + accent.
 * Returns Tailwind-compatible inline-style values.
 */
function stateStyles(
  glanceState: GlanceViewModel['glanceState'],
  dataState: GlanceViewModel['dataState'],
): { accentColor: string; tintColor: string } {
  // For error/loading/rateLimited, override to stale grey regardless of glanceState.
  if (dataState === 'loading' || dataState === 'error' || dataState === 'rateLimited') {
    return {
      accentColor: 'transparent',
      tintColor: 'transparent',
    };
  }
  switch (glanceState) {
    case 'positive':
      return {
        accentColor: 'var(--color-positive-accent)',
        tintColor: 'var(--color-positive-tint)',
      };
    case 'negative':
      return {
        accentColor: 'var(--color-negative-accent)',
        tintColor: 'var(--color-negative-tint)',
      };
    case 'alert':
      return {
        accentColor: 'var(--color-alert-accent)',
        tintColor: 'var(--color-alert-tint)',
      };
    case 'live':
      return {
        accentColor: 'var(--color-live-accent)',
        tintColor: 'transparent',
      };
    case 'stale':
      return {
        accentColor: 'var(--color-stale-accent)',
        tintColor: 'transparent',
      };
    case 'neutral':
    default:
      return { accentColor: 'transparent', tintColor: 'transparent' };
  }
}

/** Primary fact font size by tile size (glance-grammar-spec §3.1). */
function primaryFontSize(size: TileSize): string {
  if (size === '1x1') return 'var(--text-primary-1x1)';
  if (size === '2x2' || size === '3x2') return 'var(--text-primary-2x2)';
  return 'var(--text-primary-2x1)';
}

/** Relative timestamp label (glance-grammar-spec §1.1). */
function relativeTime(fetchedAt: number | null): string {
  if (fetchedAt === null) return '';
  const ageSec = Math.floor((Date.now() - fetchedAt) / 1000);
  if (ageSec < 60) return 'just now';
  if (ageSec < 3600) return `${Math.floor(ageSec / 60)}m ago`;
  return `${Math.floor(ageSec / 3600)}h ago`;
}

export function TileCard({ viewModel: vm, size, staggerIndex, editMode = false, onRemove, attribution }: TileCardProps): React.ReactElement {
  const { accentColor, tintColor } = stateStyles(vm.glanceState, vm.dataState);
  const showTimestamp = size === '2x1' || size === '2x2' || size === '3x2';
  const isStale = vm.dataState === 'stale';
  const isError = vm.dataState === 'error' || vm.dataState === 'rateLimited';
  const isLoading = vm.dataState === 'loading';
  // Two-tap confirm for remove (core-flows §3; D-011: no undo)
  const [removeConfirm, setRemoveConfirm] = useState(false);

  // Board-load stagger animation (§5.1): 40ms delay per tile.
  const animationDelay = `${staggerIndex * 40}ms`;

  return (
    <div
      role="article"
      aria-label={vm.label ?? vm.slots.primary}
      style={{
        backgroundColor: tintColor !== 'transparent' ? tintColor : 'var(--color-bg-tile)',
        border: 'var(--border-tile)',
        borderRadius: 'var(--radius-tile)',
        boxShadow: 'var(--shadow-tile)',
        padding: size === '1x1' ? 'var(--space-2)' : 'var(--space-3)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        animationDelay,
        /* @media guard is in the CSS file; inline style sets the delay. */
      }}
    >
      {/* Left-edge accent bar (§4.1): 3px, full-height, full-opacity. */}
      {accentColor !== 'transparent' && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '3px',
            backgroundColor: accentColor,
            borderRadius: 'var(--radius-tile) 0 0 var(--radius-tile)',
          }}
        />
      )}

      {/* Metadata row: label + timestamp (§1.1). */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: accentColor !== 'transparent' ? '8px' : '0',
        }}
      >
        {(size !== '1x1' && vm.label) ? (
          <span
            style={{
              fontSize: 'var(--text-label)',
              color: 'var(--color-text-dim)',
              fontWeight: 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '60%',
            }}
          >
            {vm.label}
          </span>
        ) : <span />}
        {/* Freshness timestamp + optional attribution (D-010).
             Attribution is shown only on 2×1 and 2×2 tiles where the timestamp
             row is visible (showTimestamp). Glance grammar intact: dim text only,
             no competing focal point. */}
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {showTimestamp && (
            <span
              style={{
                fontSize: 'var(--text-timestamp)',
                color: isStale ? 'var(--color-alert-accent)' : 'var(--color-text-dim)',
                fontWeight: 400,
              }}
            >
              {relativeTime(vm.fetchedAt)}
            </span>
          )}
          {showTimestamp && attribution && (
            <a
              href={attribution.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                fontSize: 'var(--text-timestamp)',
                color: 'var(--color-text-dim)',
                textDecoration: 'none',
                opacity: 0.7,
              }}
              aria-label={`Data from ${attribution.text}`}
            >
              · {attribution.text}
            </a>
          )}
        </span>
      </div>

      {/* Primary fact slot (§1.1): big, bold, center of the tile. */}
      <div
        className="tile-primary"
        style={{
          fontSize: primaryFontSize(size),
          fontWeight: 700,
          lineHeight: 1.0,
          color: isError || isLoading ? 'var(--color-text-dim)' : 'var(--color-text-primary)',
          opacity: isError && vm.fetchedAt !== null ? 0.5 : 1,
          paddingLeft: accentColor !== 'transparent' ? '8px' : '0',
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          paddingTop: 'var(--space-2)',
          paddingBottom: 'var(--space-2)',
          overflow: 'hidden',
        }}
      >
        {/* text-overflow does not apply to flex containers — the text becomes an
            anonymous flex item and hard-clips against overflow:hidden instead of
            ellipsizing. The inner span (with min-width:0 so it may shrink below
            its content) is the block that actually ellipsizes. */}
        <span
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {isLoading
            ? (vm.slots.primary || '—')
            : (vm.slots.primary || (isError ? (vm.fetchedAt !== null ? '?' : '—') : '—'))}
        </span>
      </div>

      {/* Context row: delta + state word (§1.1). */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: accentColor !== 'transparent' ? '8px' : '0',
        }}
      >
        <span
          style={{
            fontSize: 'var(--text-delta)',
            fontWeight: 500,
            color: isLoading ? 'var(--color-text-dim)' : 'var(--color-text-secondary)',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: isStale ? '70%' : '100%',
          }}
        >
          {isLoading && (vm.slots.delta === null || vm.slots.delta === '')
            ? 'Loading…'
            : isError
              ? 'Unavailable'
              : (vm.slots.delta ?? '')}
        </span>
        {isStale && (
          <span
            style={{
              fontSize: 'var(--text-timestamp)',
              color: 'var(--color-text-dim)',
              marginLeft: 'var(--space-1)',
            }}
          >
            stale
          </span>
        )}
      </div>

      {/* Remove affordance — two-tap confirm (core-flows §3; D-011: no undo). */}
      {editMode && onRemove && (
        <button
          type="button"
          aria-label={removeConfirm ? 'Confirm remove' : `Remove ${vm.label ?? 'tile'}`}
          onClick={(e) => {
            e.stopPropagation();
            if (removeConfirm) {
              onRemove();
            } else {
              setRemoveConfirm(true);
              // Auto-reset after 3s so accidental taps don't leave confirm state
              setTimeout(() => setRemoveConfirm(false), 3000);
            }
          }}
          onBlur={() => setRemoveConfirm(false)}
          style={{
            position: 'absolute',
            bottom: 6,
            right: 6,
            background: removeConfirm ? 'var(--color-negative-accent)' : 'var(--color-bg-tile)',
            color: removeConfirm ? '#fff' : 'var(--color-text-dim)',
            border: `1px solid ${removeConfirm ? 'var(--color-negative-accent)' : 'var(--color-border)'}`,
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 500,
            padding: '2px 6px',
            cursor: 'pointer',
            lineHeight: 1.4,
            zIndex: 10,
          }}
        >
          {removeConfirm ? 'Remove?' : (
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              aria-hidden="true"
              style={{ display: 'block' }}
            >
              <line x1="2" y1="2" x2="8" y2="8" />
              <line x1="8" y1="2" x2="2" y2="8" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
}
